import { StyledTopCategoriesTab } from './style';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { CategoryData } from 'types/CategoryData';
import TopCategorySelector, {
  FormDataType,
} from 'ui/Select/Selectors/TopCategorySelector';
import { Form, FormProps, message } from 'antd';
import { useIntl } from 'react-intl';
import { ImageProps } from 'types/BannerData';
import { Button } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';

type SelectedCategory = { id: number; name: string } | null;

const TopCategoriesTab = () => {
  const [allCategories, setAllCategories] = useState<CategoryData[]>([]);
  const [popular, setPopular] = useState<CategoryData[]>([]);
  const [images, setImages] = useState<ImageProps[]>([]);
  const [formData, setFormData] = useState<Record<any, FormDataType>>({});
  const [, setClearFile] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<
    Record<number, SelectedCategory>
  >({});
  const intl = useIntl();

  const [form] = Form.useForm();

  const { appendData: uploadImage } = useQueryApiClient({
    request: {
      url: '/api/v2/categories/upload',
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => {},
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/update',
      method: 'POST',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({ id: 'message.success_category_update' })
      );
    },
  });

  const { refetch: refetchImages } = useQueryApiClient({
    request: {
      url: '/api/v2/categories/popular/images',
      method: 'GET',
    },
    onSuccess: (response) => {
      setImages(response);
    },
  });

  useQueryApiClient({
    request: {
      url: 'api/v2/categories/menu/desktop',
    },
    onSuccess: (response: CategoryData[]) => {
      setAllCategories(flattenCategories(response));
    },
  });

  const { refetch: refetchPopular } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/popular',
    },
    onSuccess: (response) => {
      setPopular(response);
    },
  });

  function flattenCategories(categories: CategoryData[]): CategoryData[] {
    let flattened: CategoryData[] = [];

    categories.forEach((category) => {
      flattened.push(category);

      if (category.has_subcategories) {
        category.subcategories?.forEach((subCat) => {
          flattened.push(subCat);

          if (subCat.has_subcategories) {
            subCat.subcategories?.forEach((subSubCat) => {
              flattened.push(subSubCat);
            });
          }
        });
      }
    });

    return flattened;
  }

  const handleCategoryChange = (position: number, categoryId: number) => {
    const selected = allCategories.find((cat) => cat.id === categoryId);
    const selectedCategory = selected
      ? { id: selected.id, name: selected.name ?? '' }
      : null;

    appendData({
      top_category_position: position,
      category_id: categoryId,
    });

    setSelectedCategories((prev) => {
      const updated = { ...prev };

      Object.entries(prev).forEach(([key, selected]) => {
        const index = Number(key);

        if (index !== position && selected?.id === categoryId) {
          updated[index] = null;
        }
      });

      updated[position] = selectedCategory;

      return updated;
    });
  };

  const orderedCategories = useMemo(
    () =>
      Array.from({ length: 6 }).map((_, index) => {
        const cat =
          popular.find((cat) => cat.top_category_position === index + 1) ||
          null;

        let img = images.find((img) => img.table_row_id === index + 1) || null;

        const formDataObj = formData?.[index + 1];

        if (formDataObj && formDataObj?.file) {
          img = {
            ...img,
            url_original: formDataObj?.file
              ? URL.createObjectURL(formDataObj?.file)
              : '',
          };
        }
        return { cat: cat, img: img };
      }),
    [images, popular, formData]
  );

  const refetchData = async () => {
    await refetchPopular();
    await refetchImages();
  };

  const onFinish: FormProps['onFinish'] = useCallback(async () => {
    try {
      setClearFile(true);
      const formDataList = Object.values(formData);
      for (let i = 0; i < formDataList.length; i++) {
        const formData = new FormData();
        Object.entries(formDataList[i]).map(([key, value]) =>
          formData.append(key, value)
        );
        await uploadImage(formData);
      }
      await refetchData();
      setFormData({});
      setClearFile(false);
      message.success(
        intl.formatMessage({ id: 'message.success_save_top_category' })
      );
    } catch (e) {
      message.error(String(e));
    }
  }, [formData, refetchData, uploadImage, intl]);

  useEffect(() => {
    let initialState: Record<number, SelectedCategory> = {};

    orderedCategories.forEach(({ cat }, index) => {
      initialState[index + 1] = cat
        ? { id: cat.id, name: cat.name ?? '' }
        : null;
    });

    setSelectedCategories(initialState);
  }, [orderedCategories]);

  return (
    <StyledTopCategoriesTab>
      <Form form={form} onFinish={onFinish}>
        <div className={'levels'}>
          {orderedCategories.map(({ img }, index) => (
            <TopCategorySelector
              key={index}
              allCategories={allCategories}
              name={`${index + 1}`}
              label={`${index + 1}_category`}
              selectedCategory={selectedCategories[index + 1]}
              onCategoryChange={handleCategoryChange}
              image={img ? img : undefined}
              setFormData={setFormData}
            />
          ))}
        </div>
        <div className={'buttons'}>
          <Button
            type={'primary'}
            label={intl.formatMessage({ id: 'general.save' })}
            htmlType={'submit'}
          />
        </div>
      </Form>
    </StyledTopCategoriesTab>
  );
};

export default TopCategoriesTab;
